import { AgentType } from './agent-types';
import { User } from './user-types';

export enum FinchEventCode {
  MATTER_CREATED = 'matter_created',
  MATTER_UPDATED = 'matter_updated',
  MATTER_STATUS_CHANGED = 'matter_status_changed',
  MATTER_RETAINER_SIGNED = 'matter_retainer_signed',
  MATTER_RETAINER_SIGNED_BY_CLIENT = 'matter_retainer_signed_by_client',
  MATTER_RETAINER_SIGNED_BY_ATTORNEY = 'matter_retainer_signed_by_attorney',

  TASK_CREATED = 'task_created',
  TASK_STATUS_CHANGED = 'task_status_changed',
  TASK_ASSIGNED = 'task_assigned',
  TASK_DUE_DATE_CHANGED = 'task_due_date_changed',
  TASK_PRIORITY_CHANGED = 'task_priority_changed',
  TASK_DELETED = 'task_deleted',

  DELIVERABLE_CREATED = 'deliverable_created',
  DELIVERABLE_STATUS_CHANGED = 'deliverable_status_changed',
  DELIVERABLE_SATISFIED = 'deliverable_satisfied',
  DELIVERABLE_MATCH_CREATED = 'deliverable_match_created',
  DELIVERABLE_MATCH_STATUS_CHANGED = 'deliverable_match_status_changed',
  DELIVERABLE_MATCH_APPROVED = 'deliverable_match_approved',

  DATA_REQUIREMENT_CREATED = 'data_requirement_created',
  DATA_REQUIREMENT_STATUS_CHANGED = 'data_requirement_status_changed',
  DATA_REQUIREMENT_SATISFIED = 'data_requirement_satisfied',
  DATA_REQUIREMENT_VALUE_CREATED = 'data_requirement_value_created',
  DATA_REQUIREMENT_VALUE_APPROVED = 'data_requirement_value_approved',
  DATA_REQUIREMENT_VALUE_STATUS_CHANGED = 'data_requirement_value_status_changed',

  COVERAGE_CREATED = 'coverage_created',
  COVERAGE_UPDATED = 'coverage_updated',
  COVERAGE_STATUS_CHANGED = 'coverage_status_changed',
  COVERAGE_CATEGORY_CHANGED = 'coverage_category_changed',

  INCIDENT_CREATED = 'incident_created',
  INCIDENT_UPDATED = 'incident_updated',

  POLICE_REPORT_CREATED = 'police_report_created',
  POLICE_REPORT_UPDATED = 'police_report_updated',
  POLICE_REPORT_APPROVAL_STATUS_CHANGED = 'police_report_approval_status_changed',
  POLICE_REPORT_STATUS_CHANGED = 'police_report_status_changed',

  PROVIDER_CREATED = 'provider_created',
  PROVIDER_STATUS_CHANGED = 'provider_status_changed',
  PROVIDER_TREATMENT_STATUS_CHANGED = 'provider_treatment_status_changed',
  TREATMENT_CHECK_IN_SUBMITTED = 'treatment_check_in_submitted',
  TREATMENT_CHECK_IN_NOTE_LOGGED = 'treatment_check_in_note_logged',
  VOICE_AI_CALL_COMPLETED = 'voice_ai_call_completed',

  MATTER_CLIENT_CREATED = 'matter_client_created',
  MATTER_CLIENT_UPDATED = 'matter_client_updated',
  MATTER_CLIENT_PHONE_NUMBER_ADDED = 'matter_client_phone_number_added',

  STAFF_NOTE_CREATED = 'staff_note_created',
  STAFF_NOTE_UPDATED = 'staff_note_updated',
  STAFF_NOTE_DELETED = 'staff_note_deleted',

  ARTIFACT_CREATED = 'artifact_created',
  ARTIFACT_DIGEST_CREATED = 'artifact_digest_created',
  ARTIFACT_DIGEST_STATUS_UPDATED = 'artifact_digest_status_updated',

  BLOB_CREATED = 'blob_created',
  BLOB_DELETED = 'blob_deleted',

  TEST_EVENT = 'test_event',
  BLOB_TRANSFORM_COMPLETED = 'blob_transform_completed',

  DOCUMENT_CREATED = 'document_created',
  DOCUMENT_STATUS_CHANGED = 'document_status_changed',
  DOCUMENT_DELETED = 'document_deleted',

  // LoR
  LOR_DATA_UPDATED = 'lor_data_updated',

  MATTER_INVESTIGATION_CREATED = 'matter_investigation_created',
  MATTER_INVESTIGATION_UPDATED = 'matter_investigation_updated',
}
// End of Selection

export const DEFAULT_SILENCED_EVENTS: FinchEventCode[] = [
  FinchEventCode.BLOB_TRANSFORM_COMPLETED,
  FinchEventCode.DELIVERABLE_CREATED,
  FinchEventCode.DELIVERABLE_STATUS_CHANGED,
  FinchEventCode.DELIVERABLE_SATISFIED,
  FinchEventCode.DELIVERABLE_MATCH_STATUS_CHANGED,
  FinchEventCode.DELIVERABLE_MATCH_APPROVED,
  FinchEventCode.DATA_REQUIREMENT_CREATED,
  FinchEventCode.DATA_REQUIREMENT_STATUS_CHANGED,
  FinchEventCode.DATA_REQUIREMENT_SATISFIED,
  FinchEventCode.DATA_REQUIREMENT_VALUE_CREATED,
  FinchEventCode.DATA_REQUIREMENT_VALUE_APPROVED,
  FinchEventCode.DATA_REQUIREMENT_VALUE_STATUS_CHANGED,
  // FinchEventCode.COVERAGE_CREATED,
  FinchEventCode.ARTIFACT_CREATED,
  FinchEventCode.ARTIFACT_DIGEST_CREATED,
  FinchEventCode.ARTIFACT_DIGEST_STATUS_UPDATED,
];

export const DEFAULT_SHOWN_EVENTS: FinchEventCode[] = [
  FinchEventCode.MATTER_CREATED,
  FinchEventCode.MATTER_UPDATED,
  FinchEventCode.MATTER_STATUS_CHANGED,
  FinchEventCode.TASK_CREATED,
  FinchEventCode.TASK_STATUS_CHANGED,
  FinchEventCode.TASK_ASSIGNED,
  FinchEventCode.TASK_DELETED,
  FinchEventCode.STAFF_NOTE_CREATED,
  FinchEventCode.TREATMENT_CHECK_IN_NOTE_LOGGED,
];

/**
 * Base interface that all domain event payloads must implement
 */
export interface BaseEvent {
  /** UUID of the target entity that this event relates to */
  target_id: string;
}

/**
 * Complete domain event schema with enhanced fields from the API
 *
 * @template T The specific event code type for type-safe payload mapping
 */
export interface DomainEventSchema<T extends FinchEventCode> {
  /** Unique identifier for this event */
  id: string;

  /** Timestamp when the event actually occurred in the domain */
  occurred_at: string | null;

  /** UUID of the matter this event belongs to */
  matter_id: string;

  /** The specific type of event that occurred */
  event_code: T;

  /**
   * The actual event data payload, typed according to the event code
   * Contains the specific details of what changed or happened
   * Falls back to DefaultEventPayload for events without specific payload types
   */
  payload: import('./events/event-payload-map').PayloadForEvent<T>;

  /**
   * Content type of the target entity in "app_label.model" format
   * Example: "app.task", "app.matter", "app.deliverable"
   */
  target_type: string | null;

  /** UUID of the specific entity that this event targets */
  target_id: string | null;

  /**
   * Complete user information for the user who triggered this event
   * Null if the event was triggered by an automated agent
   */
  user: User | null;

  /**
   * Type of automated agent that triggered this event
   * Null if the event was triggered by a human user
   */
  agent: AgentType | null;

  /**
   * Correlation ID for tracking related events across workflows
   * Events with the same correlation ID are part of the same business process
   */
  correlation_id: string | null;
}

/**
 * Type alias for events with unknown/any event code
 * Useful for generic event handling where the specific type isn't known
 */
export type AnyDomainEvent = DomainEventSchema<FinchEventCode>;

/**
 * Utility type to extract the payload type for a specific event code
 */
export type EventPayload<T extends FinchEventCode> =
  DomainEventSchema<T>['payload'];

/**
 * Union type of all possible event codes for type guards and switches
 */
export type AllEventCodes = `${FinchEventCode}`;

/**
 * Type guard to check if a value is a valid FinchEventCode
 */
export function isFinchEventCode(value: string): value is FinchEventCode {
  return Object.values(FinchEventCode).includes(value as FinchEventCode);
}

/**
 * Type guard to check if an event is of a specific type
 */
export function isEventOfType<T extends FinchEventCode>(
  event: AnyDomainEvent,
  eventCode: T,
): event is DomainEventSchema<T> {
  return event.event_code === eventCode;
}

/**
 * Usage Examples:
 *
 * // Events with specific payloads work as before:
 * const matterEvent: DomainEventSchema<FinchEventCode.MATTER_CREATED> = {
 *   // ... event fields
 *   payload: { title: "New case", client_name: "John Doe" } // MatterCreatedEvent
 * };
 *
 * // Events without specific payloads use BaseEvent automatically:
 * const archiveEvent: DomainEventSchema<FinchEventCode.MATTER_ARCHIVED> = {
 *   // ... event fields
 *   payload: { target_id: "matter-123" } // BaseEvent
 * };
 *
 * // You can add new event codes without defining payloads:
 * // Just add to FinchEventCode enum and they'll use BaseEvent automatically
 */
